import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Modal,
  Typography,
  LinearProgress,
} from "@mui/material";
import XLSX from "xlsx-js-style";
import { FilialSelect, FilialDivisionSelect2 } from "../../../components/Select/domainSelects";
import { accreditationIndicatorCRUD, filialCRUD, filialDivisionCRUD } from "../../../http";
import { toLocalCalendarDateString } from "../../../functions/dateHelpers";

const FILIAL_DIVISION_MATCH_WORDS = 5;
const FILIAL_DIVISION_FUZZY_MIN = 0.6;

const normalizeWordsKey = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^a-zа-яё0-9\s]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

// Убираем часто «лишние» слова, которые в Excel могут отсутствовать: "районное отделение"
const stripDivisionNoise = (s) =>
  normalizeWordsKey(s)
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !["район", "районное", "районного", "отделение", "отделения", "ро", "р", "о"].includes(w))
    .join(" ");

const firstWordsKey = (s, wordCount = FILIAL_DIVISION_MATCH_WORDS) => {
  const parts = stripDivisionNoise(s).split(/\s+/).filter(Boolean);
  return parts.slice(0, wordCount).join(" ");
};

const levenshtein = (a, b) => {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
};

const stringSimilarity = (a, b) => {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  if (a === b) return 1;
  const maxLen = Math.max(a.length, b.length);
  return 1 - levenshtein(a, b) / maxLen;
};

const stripFilialNoise = (s) =>
  normalizeWordsKey(s)
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !["филиал", "филиала", "филиалы", "рнк", "ргп"].includes(w))
    .join(" ");

/** У филиалов НЦЭ общий длинный префикс; первые 5 слов совпадают у всех — нельзя резать по firstWordsKey */
const extractNceFilialTail = (s) => {
  const raw = String(s || "").trim();
  const tryMatch = (str) => {
    const m = str.match(/ксэк\s+мз\s+рк\s+по\s+(.+)$/i);
    return m?.[1] ? m[1].trim().replace(/^["«»]+|["«»]+$/g, "").trim() : null;
  };
  return tryMatch(raw) || tryMatch(normalizeWordsKey(raw));
};

const matchFilialId = (text, filials) => {
  const t = String(text || "").trim();
  if (!t) return { id: null };

  const n = normalizeWordsKey(t);
  if (!n) return { error: "Пустой текст филиала" };

  // 1) Полное совпадение нормализованного названия (как в справочнике)
  const exactFull = filials.filter((f) => normalizeWordsKey(f.name) === n);
  if (exactFull.length === 1) return { id: exactFull[0].id };
  if (exactFull.length > 1) {
    return { error: "Неоднозначно: в справочнике несколько филиалов с одинаковым названием." };
  }

  // 2) Совпадение по «хвосту» после «… КСЭК МЗ РК по …» (различаются только регион/город)
  const tailExcel = extractNceFilialTail(t);
  if (tailExcel) {
    const nt = normalizeWordsKey(tailExcel);
    const byTail = filials.filter((f) => {
      const tailF = extractNceFilialTail(f.name);
      return tailF && normalizeWordsKey(tailF) === nt;
    });
    if (byTail.length === 1) return { id: byTail[0].id };
    if (byTail.length > 1) {
      return {
        error: `Неоднозначно по региону «${tailExcel}»: ${byTail.map((f) => f.name).join("; ")}`,
      };
    }
  }

  // 3) В Excel только короткий хвост (без префикса НЦЭ) — конец полного названия в справочнике
  if (n.length >= 6) {
    const bySuffix = filials.filter((f) => {
      const fn = normalizeWordsKey(f.name);
      return fn.endsWith(n) || n.endsWith(fn);
    });
    if (bySuffix.length === 1) return { id: bySuffix[0].id };
  }

  // 4) Одна строка содержит другую (только если фрагмент достаточно длинный)
  if (n.length >= 8) {
    const bySub = filials.filter((f) => {
      const fn = normalizeWordsKey(f.name);
      if (fn.length >= n.length) return fn.includes(n);
      return n.includes(fn);
    });
    if (bySub.length === 1) return { id: bySub[0].id };
  }

  // 5) Fuzzy по полной строке (не по первым 5 словам)
  const excelFull = stripFilialNoise(t);
  const scored = (filials || [])
    .map((f) => {
      const fnFull = stripFilialNoise(f.name);
      return { f, score: stringSimilarity(excelFull, fnFull) };
    })
    .filter((x) => x.score >= FILIAL_DIVISION_FUZZY_MIN)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return { error: `Филиал не найден по тексту: «${t}»` };
  if (scored.length === 1) return { id: scored[0].f.id };

  const gap = scored[0].score - scored[1].score;
  if (gap < 0.03) {
    return {
      error: `Неоднозначно (похожие названия): ${scored
        .slice(0, 3)
        .map((x) => `${x.f.name} (${x.score.toFixed(2)})`)
        .join("; ")}`,
    };
  }
  return { id: scored[0].f.id };
};

const matchFilialDivisionId = (text, divisions) => {
  const t = String(text || "").trim();
  if (!t) return { id: null };

  const targetKey = firstWordsKey(t);
  if (!targetKey) return { error: "Пустой текст районного отделения" };

  const exact = divisions.filter((d) => firstWordsKey(d.name) === targetKey);
  if (exact.length === 1) return { id: exact[0].id };
  if (exact.length > 1) {
    return { error: `Неоднозначно: ${exact.map((d) => d.name).join("; ")}` };
  }

  const excelFull = stripDivisionNoise(t);
  const scored = (divisions || [])
    .map((d) => {
      const divFull = stripDivisionNoise(d.name);
      const divKey = firstWordsKey(d.name);
      const score = Math.max(
        stringSimilarity(excelFull, divFull),
        stringSimilarity(targetKey, divKey)
      );
      return { d, score };
    })
    .filter((x) => x.score >= FILIAL_DIVISION_FUZZY_MIN)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return { error: `Районное отделение не найдено по тексту: «${t}»` };
  if (scored.length === 1) return { id: scored[0].d.id };

  const gap = scored[0].score - scored[1].score;
  if (gap < 0.03) {
    return {
      error: `Неоднозначно (похожие названия): ${scored
        .slice(0, 3)
        .map((x) => `${x.d.name} (${x.score.toFixed(2)})`)
        .join("; ")}`,
    };
  }
  return { id: scored[0].d.id };
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const toDateOrNull = (v) => {
  if (v == null || v === "") return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
  if (typeof v === "number") {
    // excel serial date
    const parsed = XLSX.SSF?.parse_date_code?.(v);
    if (parsed && parsed.y && parsed.m && parsed.d) {
      return new Date(parsed.y, parsed.m - 1, parsed.d);
    }
  }
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;
    // dd.mm.yyyy
    const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (m) {
      const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const toBoolOrNull = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const s = String(v).trim().toLowerCase();
  if (!s) return null;
  if (["да", "yes", "y", "true", "1"].includes(s)) return true;
  if (["нет", "no", "n", "false", "0"].includes(s)) return false;
  return null;
};

const normalizeAddressArray = (v) => {
  if (v == null || v === "") return null;
  if (Array.isArray(v)) {
    const cleaned = v.map((x) => String(x ?? "").trim()).filter(Boolean);
    return cleaned.length ? cleaned : null;
  }
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;
    if (s.startsWith("[") || s.startsWith("{")) {
      try {
        return normalizeAddressArray(JSON.parse(s));
      } catch (e) {
        // fall through
      }
    }
    // IMPORTANT: do not split by comma (commas are part of addresses). Use newline / ';' / '|'
    const parts = s
      .split(/\r?\n|[;|]/g)
      .map((p) => p.trim())
      .filter(Boolean);
    return parts.length ? parts : [s];
  }
  // object/number/etc → store as single JSON-ish line
  try {
    return [JSON.stringify(v)];
  } catch (e) {
    return [String(v)];
  }
};

// Try to detect header row and map columns by name. If not found, use fixed indexes.
const detectColumnMap = (aoa) => {
  const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const wanted = [
    { key: "certificateNumber", variants: ["номер аттестата", "номер аттестата аккредитации", "№ аттестата", "аттестат"] },
    { key: "name", variants: ["название", "наименование"] },
    { key: "issueDate", variants: ["дата выдачи", "дата выдачи аттестата"] },
    { key: "validityPeriod", variants: ["срок действия", "действителен до", "действует до"] },
    { key: "address", variants: ["адрес", "адреса"] },
    { key: "isIrrelevant", variants: ["неактуальный", "не актуальный", "неактуален"] },
    {
      key: "filialDivision",
      variants: ["районное отделение", "районное отд", "район отдел", "р/о", "р.о.", "р о", "район"],
    },
    {
      key: "filial",
      variants: ["филиал", "филиала", "наименование филиала", "филиал (название)"],
    },
  ];

  for (let r = 0; r < Math.min(aoa.length, 30); r++) {
    const row = aoa[r] || [];
    const normalizedCells = row.map(norm);
    const found = {};
    wanted.forEach((w) => {
      const idx = normalizedCells.findIndex((c) =>
        w.variants.some((v) => c.includes(norm(v)))
      );
      if (idx >= 0) found[w.key] = idx;
    });
    if (found.certificateNumber != null || found.name != null) {
      return { headerRow: r, map: found };
    }
  }

  // fallback:
  // 0: №, 1: certificateNumber, 2: name, 3: issueDate, 4: validityPeriod, 5: address, 6: isIrrelevant, 7: filialDivision, 8: filial
  return {
    headerRow: 0,
    map: {
      certificateNumber: 1,
      name: 2,
      issueDate: 3,
      validityPeriod: 4,
      address: 5,
      isIrrelevant: 6,
      filialDivision: 7,
      filial: 8,
    },
  };
};

const pick = (row, idx) => {
  if (idx == null) return "";
  return row?.[idx] ?? "";
};

export default function AccreditationMarkIndicatorImportExcelModal({
  open,
  onClose,
  onDone,
}) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [parseInfo, setParseInfo] = useState(null);

  const [filialId, setFilialId] = useState("");
  const [filialDivisionId, setFilialDivisionId] = useState("");

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errors, setErrors] = useState([]);

  const preview = useMemo(() => rows.slice(0, 5), [rows]);

  const reset = () => {
    setFileName("");
    setRows([]);
    setParseInfo(null);
    setErrors([]);
    setUploading(false);
    setProgress({ done: 0, total: 0 });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setErrors([]);

    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array", cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });

    const detected = detectColumnMap(aoa);
    setParseInfo(detected);

    const startRow = detected.headerRow + 1;
    const parsed = [];

    for (let r = startRow; r < aoa.length; r++) {
      const row = aoa[r];
      if (!row) continue;

      const certificateNumber = String(pick(row, detected.map.certificateNumber) || "").trim();
      const name = String(pick(row, detected.map.name) || "").trim();
      const addressRaw = pick(row, detected.map.address);
      const filialDivisionRaw = pick(row, detected.map.filialDivision);
      const filialDivisionText =
        filialDivisionRaw == null || String(filialDivisionRaw).trim() === ""
          ? null
          : String(filialDivisionRaw).trim();

      const filialRaw = pick(row, detected.map.filial);
      const filialText =
        filialRaw == null || String(filialRaw).trim() === "" ? null : String(filialRaw).trim();

      // skip fully empty lines
      if (!certificateNumber && !name && (addressRaw == null || String(addressRaw).trim() === "")) continue;

      const item = {
        __row: r + 1,
        certificateNumber: certificateNumber || null,
        name: name || null,
        issueDate: toDateOrNull(pick(row, detected.map.issueDate)),
        validityPeriod: toDateOrNull(pick(row, detected.map.validityPeriod)),
        address: normalizeAddressArray(addressRaw),
        isIrrelevant: toBoolOrNull(pick(row, detected.map.isIrrelevant)),
        filialDivisionText,
        filialText,
      };

      parsed.push(item);
    }

    setRows(parsed);
  };

  const upload = async () => {
    const errs = [];
    const needDefaultFilial = rows.some((r) => !r.filialText);
    if (needDefaultFilial && !filialId) errs.push("Выберите филиал или укажите колонку «Филиал» в Excel для каждой строки.");
    if (!rows.length) errs.push("Нет строк для импорта (проверьте Excel).");
    if (errs.length) {
      alert(errs.join("\n"));
      return;
    }

    setUploading(true);
    setErrors([]);
    setProgress({ done: 0, total: rows.length });

    let filials = [];
    try {
      const fRes = await filialCRUD.search({
        paging: { skip: 0, take: 10000 },
      });
      filials = fRes?.result || [];
    } catch (e) {
      setUploading(false);
      alert(`Не удалось загрузить справочник филиалов: ${e}`);
      return;
    }

    const divisionsCache = {};
    const getDivisionsForFilial = async (fid) => {
      const key = String(fid);
      if (divisionsCache[key]) return divisionsCache[key];
      const fdRes = await filialDivisionCRUD.search({
        paging: { skip: 0, take: 10000 },
        filter: { filialId: { operand1: Number(fid), operator: "equals" } },
      });
      divisionsCache[key] = fdRes?.result || [];
      return divisionsCache[key];
    };

    const outErrors = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      try {
        if (!r.certificateNumber) throw new Error("Пустое поле 'Номер аттестата'");

        let resolvedFilialId = null;
        if (r.filialText) {
          const fm = matchFilialId(r.filialText, filials);
          if (fm.error) throw new Error(fm.error);
          resolvedFilialId = fm.id;
        } else {
          resolvedFilialId = Number(filialId);
        }
        if (!resolvedFilialId) throw new Error("Не удалось определить филиал для строки");

        const divisions = await getDivisionsForFilial(resolvedFilialId);

        const rowUsesExcelFilial = Boolean(r.filialText);
        let resolvedFilialDivisionId = null;
        const excelRo = r.filialDivisionText && String(r.filialDivisionText).trim();
        if (excelRo) {
          const m = matchFilialDivisionId(excelRo, divisions);
          if (m.error) throw new Error(m.error);
          resolvedFilialDivisionId = m.id;
        } else if (!rowUsesExcelFilial && filialDivisionId) {
          resolvedFilialDivisionId = Number(filialDivisionId);
        }

        const payload = {
          certificateNumber: r.certificateNumber,
          name: r.name,
          issueDate: toLocalCalendarDateString(r.issueDate),
          validityPeriod: toLocalCalendarDateString(r.validityPeriod),
          address: r.address,
          isIrrelevant: r.isIrrelevant ?? false,
          state: 1,
          filialId: resolvedFilialId,
          filialDivisionId: resolvedFilialDivisionId,
        };

        await accreditationIndicatorCRUD.create(payload);
      } catch (e) {
        outErrors.push({ row: r.__row, message: String(e) });
      } finally {
        setProgress((p) => ({ ...p, done: i + 1 }));
      }
    }

    setErrors(outErrors);
    setUploading(false);
    onDone && onDone({ total: rows.length, errors: outErrors.length });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Импорт Excel — индикатор реестра знака аккредитации
        </Typography>

        <Typography sx={{ mb: 2, color: "text.secondary" }}>
          Поддерживаемые колонки: Номер аттестата, Название, Дата выдачи, Срок действия, Адрес, Неактуальный,
          Районное отделение, <b>Филиал</b> (название из справочника — подбирается по тексту, как РО).
          Если в строке указан филиал в Excel, для неё берётся он; иначе — филиал, выбранный в форме выше.
          Районное отделение: если колонка в строке заполнена — сопоставляется с РО этого филиала; иначе для строк
          без филиала в Excel подставляется выбранное в форме РО.
          Для нескольких адресов в одной ячейке используйте перенос строки, символ “;” или “|”.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FilialSelect
              value={filialId}
              onChange={(id) => {
                setFilialId(id);
                setFilialDivisionId("");
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FilialDivisionSelect2
              filialId={filialId}
              value={filialDivisionId}
              onChange={(val) => setFilialDivisionId(val)}
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="outlined" component="label" disabled={uploading}>
              Выбрать Excel
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFile}
              />
            </Button>
            {fileName ? (
              <Typography sx={{ ml: 2, display: "inline-block" }}>
                {fileName}
              </Typography>
            ) : null}
          </Grid>

          {rows.length ? (
            <Grid item xs={12}>
              <Typography sx={{ mb: 1 }}>
                Найдено строк: <b>{rows.length}</b> (показано первые 5)
              </Typography>
              <Box
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  p: 2,
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {preview
                  .map((r) => {
                    const addr = Array.isArray(r.address) ? r.address.join(" | ") : "";
                    const fi = r.filialText ? ` | филиал: ${r.filialText}` : "";
                    return `#${r.__row} ${r.certificateNumber || ""} | ${r.name || ""}${fi} | ${addr}`;
                  })
                  .join("\n")}
              </Box>
            </Grid>
          ) : null}

          {uploading ? (
            <Grid item xs={12}>
              <Typography sx={{ mb: 1 }}>
                Загрузка: {progress.done}/{progress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
              />
            </Grid>
          ) : null}

          {errors.length ? (
            <Grid item xs={12}>
              <Typography color="error" sx={{ mb: 1 }}>
                Ошибки: {errors.length}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #f2c2c2",
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 200,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {errors.map((e) => `Строка ${e.row}: ${e.message}`).join("\n")}
              </Box>
            </Grid>
          ) : null}

          <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Button variant="contained" onClick={upload} disabled={uploading || !rows.length}>
              Загрузить в систему
            </Button>
            <Button variant="outlined" onClick={handleClose} disabled={uploading}>
              Закрыть
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

